/**
 * DISC personality types — the ONE place the app names them.
 *
 * Most advisers and every client do not know what "D" or "老鷹型" means, so a
 * type is named by how that person decides (果斷決策型…) and hinted with what
 * they value and what to avoid with them (重視結果 · 避免拖泥帶水…). The letter
 * and the animal emoji stay as small marks for those who do know DISC.
 *
 * Kept in TS rather than the locale JSON because the public quiz pages
 * (CareerQuiz, CoverQuiz) render without react-intl; every surface — the
 * adviser app included — reads the names from here so they cannot drift.
 *
 *   discName  → 果斷決策型                                  (a name inside a sentence)
 *   discTag   → 🦅 果斷決策型                               (where space is tight, e.g. pairing pills)
 *   discLabel → 🦅 果斷決策型 (D)                           (chips and labels with room for the letter)
 *   discHint  → 重視結果 · 避免拖泥帶水                     (the hint)
 *   discFull  → 🦅 果斷決策型 (D) · 重視結果 · 避免拖泥帶水 (pickers, tooltips)
 *
 * Headings show the letter as a <DiscLetterBadge> after the name instead.
 */

export type DiscLetter = 'D' | 'I' | 'S' | 'C';
export type DiscLocale = 'zh-Hant-HK' | 'zh' | 'en';

export const DISC_LETTERS: DiscLetter[] = ['D', 'I', 'S', 'C'];

export const DISC_EMOJI: Record<DiscLetter, string> = {
  D: '🦅',
  I: '🦚',
  S: '🕊️',
  C: '🦉'
};

const DISC_TEXT: Record<DiscLocale, { names: Record<DiscLetter, string>; hints: Record<DiscLetter, string> }> = {
  'zh-Hant-HK': {
    names: { D: '果斷決策型', I: '熱情分享型', S: '穩健守護型', C: '精明分析型' },
    hints: {
      D: '重視結果 · 避免拖泥帶水',
      I: '重視感覺 · 避免只講數字',
      S: '重視安穩 · 避免催促決定',
      C: '重視數據 · 避免誇大含糊'
    }
  },
  zh: {
    names: { D: '果断决策型', I: '热情分享型', S: '稳健守护型', C: '精明分析型' },
    hints: {
      D: '重视结果 · 避免拖泥带水',
      I: '重视感觉 · 避免只讲数字',
      S: '重视安稳 · 避免催促决定',
      C: '重视数据 · 避免夸大含糊'
    }
  },
  en: {
    names: { D: 'Decisive Driver', I: 'Enthusiastic Sharer', S: 'Steady Guardian', C: 'Sharp Analyst' },
    hints: {
      D: 'Values results · Avoid dragging on',
      I: 'Values feelings · Avoid numbers-only talk',
      S: 'Values security · Avoid rushing decisions',
      C: 'Values data · Avoid hype and vagueness'
    }
  }
};

export const isDiscLetter = (value: unknown): value is DiscLetter =>
  typeof value === 'string' && (DISC_LETTERS as string[]).includes(value);

/** App locales are 'zh-Hant-HK' / 'zh' / 'en'; anything unknown reads as zh-Hant-HK. */
export const normalizeDiscLocale = (locale?: string | null): DiscLocale => {
  const lower = (locale || '').toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower === 'zh' || lower.startsWith('zh-cn') || lower.startsWith('zh-hans')) return 'zh';
  return 'zh-Hant-HK';
};

// Each helper returns '' for anything that is not a DISC letter, so callers can
// pass a raw factsheet value straight through.

export const discName = (letter: unknown, locale?: string | null): string =>
  isDiscLetter(letter) ? DISC_TEXT[normalizeDiscLocale(locale)].names[letter] : '';

export const discHint = (letter: unknown, locale?: string | null): string =>
  isDiscLetter(letter) ? DISC_TEXT[normalizeDiscLocale(locale)].hints[letter] : '';

export const discTag = (letter: unknown, locale?: string | null): string =>
  isDiscLetter(letter) ? `${DISC_EMOJI[letter]} ${discName(letter, locale)}` : '';

export const discLabel = (letter: unknown, locale?: string | null): string =>
  isDiscLetter(letter) ? `${discTag(letter, locale)} (${letter})` : '';

export const discFull = (letter: unknown, locale?: string | null): string =>
  isDiscLetter(letter) ? `${discLabel(letter, locale)} · ${discHint(letter, locale)}` : '';
